(() => {
  'use strict';
  const root = document.getElementById('mv-website-directory');
  if (!root) return;
  const collator = new Intl.Collator('en-CA', { sensitivity: 'base' });
  const excludedPaths = new Set(['/products/mville-vip-membership']);
  const lists = {
    mattresses: root.querySelector('#directory-mattresses'),
    accessories: root.querySelector('#directory-accessories'),
    furniture: root.querySelector('#directory-furniture'),
    adjustable: root.querySelector('#directory-adjustable'),
    other: root.querySelector('#directory-other'),
    collections: root.querySelector('#directory-collection-links'),
    pages: root.querySelector('#directory-page-links'),
    blogs: root.querySelector('#directory-blog-links'),
    articles: root.querySelector('#directory-article-links')
  };
  const seed = new Map();
  for (const list of Object.values(lists)) {
    for (const item of list.children) {
      const link = item.querySelector('a[href]');
      if (!link) continue;
      const url = new URL(link.href, location.origin);
      seed.set(url.pathname, {
        title: link.textContent.trim(),
        group: item.dataset.directoryGroup,
        type: item.dataset.productType || ''
      });
    }
    const items = Array.from(list.children);
    items.sort((a, b) => collator.compare(a.textContent.trim(), b.textContent.trim()));
    list.replaceChildren(...items);
  }

  const readableHandle = path => decodeURIComponent(path.split('/').filter(Boolean).pop() || '')
    .replace(/[-_]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  const family = path => {
    if (/^\/products\/[^/]+\/?$/.test(path)) return 'products';
    if (/^\/collections\/[^/]+\/?$/.test(path)) return 'collections';
    if (/^\/pages\/[^/]+\/?$/.test(path)) return 'pages';
    if (/^\/blogs\/[^/]+\/[^/]+\/?$/.test(path)) return 'articles';
    if (/^\/blogs\/[^/]+\/?$/.test(path)) return 'blogs';
    return null;
  };
  const productGroup = record => {
    const previous = seed.get(record.path);
    if (previous && previous.group) return previous.group;
    const name = record.title.toLowerCase();
    if (/adjustable|reflexion/.test(name)) return 'adjustable';
    if (/box spring|foundation|platform|bed frame|wood bed|metal frame/.test(name)) return 'furniture';
    if (/protector|topper|sheet|bolster/.test(name)) return 'accessories';
    if (/mattress/.test(name)) return 'mattresses';
    if (/pillow|bedding/.test(name)) return 'accessories';
    if (/bed|frame|platform/.test(name)) return 'furniture';
    return 'other';
  };
  const directText = (element, name) => Array.from(element.children)
    .find(child => child.localName === name)?.textContent.trim() || '';
  const readXml = async address => {
    const url = new URL(address, location.origin);
    if (url.origin !== location.origin) throw new Error('Unexpected sitemap origin');
    const response = await fetch(url, { credentials: 'omit', cache: 'no-cache' });
    if (!response.ok) throw new Error('Sitemap refresh unavailable');
    const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
    if (xml.querySelector('parsererror')) throw new Error('Invalid sitemap XML');
    return xml;
  };
  const makeList = (list, records) => {
    records.sort((a, b) => collator.compare(a.title, b.title));
    const fragment = document.createDocumentFragment();
    for (const record of records) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = record.path;
      link.textContent = record.title;
      item.append(link);
      fragment.append(item);
    }
    list.replaceChildren(fragment);
  };
  const refresh = async () => {
    const index = await readXml('/sitemap.xml');
    if (index.documentElement.localName !== 'sitemapindex') return;
    const sources = Array.from(index.getElementsByTagNameNS('*', 'sitemap'))
      .map(node => directText(node, 'loc'))
      .map(address => {
        const url = new URL(address, location.origin);
        const match = url.pathname.match(/^\/sitemap_(products|collections|pages|blogs|articles)_\d+\.xml$/);
        return match && url.origin === location.origin ? { address: url.href, kind: match[1] } : null;
      }).filter(Boolean);
    const results = new Array(sources.length);
    let next = 0;
    const worker = async () => {
      while (next < sources.length) {
        const position = next++;
        const source = sources[position];
        try {
          const xml = await readXml(source.address);
          if (xml.documentElement.localName !== 'urlset') throw new Error('Invalid URL set');
          const records = [];
          for (const node of xml.getElementsByTagNameNS('*', 'url')) {
            const address = directText(node, 'loc');
            if (!address) continue;
            const url = new URL(address, location.origin);
            if (url.origin !== location.origin || family(url.pathname) !== source.kind || excludedPaths.has(url.pathname.replace(/\/$/, ''))) continue;
            const old = seed.get(url.pathname);
            const xmlTitle = node.getElementsByTagNameNS('http://www.google.com/schemas/sitemap-image/1.1', 'title')[0]?.textContent.trim();
            records.push({ path: url.pathname, title: old?.title || xmlTitle || readableHandle(url.pathname) });
          }
          results[position] = { kind: source.kind, records, valid: true };
        } catch (_) {
          results[position] = { kind: source.kind, valid: false };
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, sources.length) }, worker));
    for (const kind of ['products', 'collections', 'pages', 'blogs', 'articles']) {
      const parts = results.filter(result => result?.kind === kind);
      if (!parts.length || parts.some(part => !part.valid)) continue;
      const records = Array.from(new Map(parts.flatMap(part => part.records).map(record => [record.path, record])).values());
      if (kind === 'products') {
        for (const group of ['mattresses', 'accessories', 'furniture', 'adjustable', 'other']) {
          const grouped = records.filter(record => productGroup(record) === group);
          makeList(lists[group], grouped);
          lists[group].closest('section').hidden = grouped.length === 0;
        }
      } else {
        makeList(lists[kind], records);
      }
    }
  };
  // The complete server-rendered directory stays usable if a refresh is unavailable.
  refresh().catch(() => {});
})();
